/**
 * Client-side session settings: the user's idle timeout and the last observed activity.
 *
 * Nothing here is a security decision. The idle timeout is only a *request* for how long
 * a session may stay idle before the server expires it (`POST /api/auth/restore` sends
 * `idle_minutes`); the server enforces and owns the value. `mb-last-activity` is a purely
 * local convenience used by the UI to decide whether to ask for a restore, and it is
 * validated before use so a corrupted or tampered value simply means "unknown".
 *
 * Every read and write is best-effort: `localStorage` may be missing (SSR, tests),
 * disabled (private mode, policy) or full. None of those conditions may break the app,
 * so all access is swallowed and turned into a sensible default.
 */

/** The idle timeout choices offered in the UI, in minutes. */
export const IDLE_OPTIONS = [5, 15, 30, 60] as const;

/** Used when nothing valid is stored. */
export const DEFAULT_IDLE_MINUTES = 5;

/** Writing activity at most once per this window keeps `localStorage` writes cheap. */
export const ACTIVITY_THROTTLE_MS = 10_000;

/** Storage key for the selected idle timeout (minutes). */
const IDLE_KEY = "mb-idle-minutes";

/** Storage key for the JSON `{u, t}` record of the last activity. */
const ACTIVITY_KEY = "mb-last-activity";

/** Clock skew tolerated on the stored activity timestamp (a slightly future `t`). */
const MAX_CLOCK_SKEW_MS = 5000;

/**
 * The minimal synchronous storage surface this module needs. It matches the `Store`
 * shape used by `chatDraft.ts`; `window.localStorage` satisfies it.
 */
export interface Store {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export type IdleMinutes = (typeof IDLE_OPTIONS)[number];

function asStore(store: Store | null | undefined): Store | null {
  return store ?? null;
}

function read(store: Store | null | undefined, key: string): string | null {
  const target = asStore(store);
  if (!target) return null;
  try {
    const value = target.getItem(key);
    return typeof value === "string" ? value : null;
  } catch {
    return null;
  }
}

function write(store: Store | null | undefined, key: string, value: string): boolean {
  const target = asStore(store);
  if (!target) return false;
  try {
    target.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function remove(store: Store | null | undefined, key: string): void {
  const target = asStore(store);
  if (!target) return;
  try {
    target.removeItem(key);
  } catch {
    // Ignored: a failing store must never break the caller.
  }
}

function isIdleMinutes(value: unknown): value is IdleMinutes {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    (IDLE_OPTIONS as readonly number[]).includes(value)
  );
}

/** The stored idle timeout, or `DEFAULT_IDLE_MINUTES` when absent or invalid. */
export function loadIdleMinutes(store: Store | null | undefined): number {
  const raw = read(store, IDLE_KEY);
  if (raw === null) return DEFAULT_IDLE_MINUTES;
  const minutes = Number(raw.trim());
  return isIdleMinutes(minutes) ? minutes : DEFAULT_IDLE_MINUTES;
}

/**
 * Persist the idle timeout. Only a valid `IDLE_OPTIONS` value is written; anything else
 * is refused without touching storage. Returns whether the value is now stored.
 */
export function saveIdleMinutes(
  store: Store | null | undefined,
  minutes: number,
): boolean {
  if (!isIdleMinutes(minutes)) return false;
  return write(store, IDLE_KEY, String(minutes));
}

interface ActivityRecord {
  u: string;
  t: number;
}

function parseActivity(raw: string | null): ActivityRecord | null {
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const record = parsed as { u?: unknown; t?: unknown };
  if (typeof record.u !== "string" || typeof record.t !== "number") return null;
  return { u: record.u, t: record.t };
}

/**
 * The stored activity timestamp for `userId`, or `null` when missing, malformed,
 * belonging to another user, not a positive integer, or implausibly in the future
 * (more than {@link MAX_CLOCK_SKEW_MS} ahead of `now`).
 */
export function lastActivity(
  store: Store | null | undefined,
  userId: string,
  now: number,
): number | null {
  const record = parseActivity(read(store, ACTIVITY_KEY));
  if (record === null) return null;
  if (record.u !== userId) return null;
  if (!Number.isInteger(record.t) || record.t <= 0) return null;
  if (record.t > now + MAX_CLOCK_SKEW_MS) return null;
  return record.t;
}

/**
 * Record activity for `userId` at `now`.
 *
 * Writes are throttled: a record for the same user whose age is within
 * `[0, ACTIVITY_THROTTLE_MS)` is left alone. A write happens for another user, when the
 * clock moved backwards (`now < t`), or once the throttle window has elapsed.
 */
export function markActivity(
  store: Store | null | undefined,
  userId: string,
  now: number,
): void {
  const target = asStore(store);
  if (!target || typeof userId !== "string" || !Number.isFinite(now)) return;

  const record = parseActivity(read(target, ACTIVITY_KEY));
  if (record !== null && record.u === userId && Number.isInteger(record.t)) {
    const age = now - record.t;
    if (age >= 0 && age < ACTIVITY_THROTTLE_MS) return;
  }

  write(target, ACTIVITY_KEY, JSON.stringify({ u: userId, t: now }));
}

/** Forget the stored activity timestamp (e.g. on sign-out). */
export function clearActivity(store: Store | null | undefined): void {
  remove(store, ACTIVITY_KEY);
}
